import { useTranslation } from 'react-i18next';

import { Input, Pagination, Spin } from '@arco-design/web-react';

import { EmptyTips } from '@/components';
import { RepoSelectorCard } from '@/components-biz/RepoSelectorCard';
import type { MaasModelSourceStackScope } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';

import { useAvailableModelRepo } from './hooks/useAvailableModelRepo';
import styles from './index.module.less';
import type { AvailableModelRepoItem } from './type';
import { getArtifactModelSizeBytes, getStackScopeSourceFromArtifact, isStackScopeSourceMatch } from './utils';

export { getArtifactModelSizeBytes, getStackScopeSourceFromArtifact, isStackScopeSourceMatch } from './utils';
export { useAvailableModelRepo } from './hooks/useAvailableModelRepo';
export { getAvailableModelRepos } from './service';
export type { AvailableModelRepoItem } from './type';

export interface AvailableModeRepoProps {
  disabled?: boolean;
  /** 与 model.source.stackscope 对齐 */
  value?: Partial<MaasModelSourceStackScope>;
  onChange?: (source: MaasModelSourceStackScope, item: AvailableModelRepoItem) => void;
}

/** 模型仓库选择（对应后端 model.source.stackscope） */
export function AvailableModeRepo({ disabled = false, value, onChange }: AvailableModeRepoProps) {
  const { t } = useTranslation();
  const { items, loading, page, limit, total, inputValue, onInputChange, onSearch, onPageChange } = useAvailableModelRepo();

  const handleSelect = (item: AvailableModelRepoItem) => {
    if (disabled) return;
    const source = getStackScopeSourceFromArtifact(item);
    if (!source.model || !source.url) return;
    onChange?.(source, item);
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchBar}>
        <h5 className={styles.title}>{t('hooks:model.repository')}</h5>
        <Input.Search
          placeholder={t('data-center:maas.model.stackscope.search')}
          allowClear
          value={inputValue}
          onChange={onInputChange}
          onSearch={onSearch}
          onClear={() => onSearch('')}
          disabled={disabled}
          style={{ width: '40%' }}
        />
      </div>

      <Spin loading={loading} block>
        <div className={styles.repoGrid}>
          {items.map((item) => {
            const source = getStackScopeSourceFromArtifact(item);
            const sizeBytes = getArtifactModelSizeBytes(item);
            const selected = isStackScopeSourceMatch(item, value);
            return (
              <RepoSelectorCard
                key={item.id}
                name={item.name}
                metaItems={[source.model, sizeBytes]}
                address={source.url}
                selected={selected}
                disabled={disabled}
                onClick={() => handleSelect(item)}
              />
            );
          })}
        </div>

        {!loading && items.length === 0 ? (
          <div className={styles.emptyWrapper}>
            <EmptyTips tips={t('data-center:maas.model.stackscope.empty')} />
          </div>
        ) : null}

        {total > limit ? (
          <div className={styles.paginationWrapper}>
            <Pagination size="mini" current={page} pageSize={limit} total={total} onChange={onPageChange} showTotal disabled={disabled} />
          </div>
        ) : null}
      </Spin>
    </div>
  );
}
